import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { C, S, BASE } from '../constants';
import { fmt, fmtUAH, exportXlsx } from '../utils';
import Img from '../components/Img';
import ProdLink from '../components/ProdLink';
import CostInput from '../components/CostInput';
import InfoTip from '../components/InfoTip';
import { ProductStatusBadge, FfBadge, AbcBadge, DaysBadge, ProfitBadge, Badge } from '../components/badges';
import ProductHistoryModal from '../components/ProductHistoryModal';

function LeadTimeCell({ rozetkaId, value, alertDays, token }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const save = () => {
    const days = parseInt(val);
    if (!isNaN(days) && days >= 0) {
      fetch(`${BASE}/api/products/${rozetkaId}/lead-time`, {
        method: "PATCH", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ lead_time_days: days }),
      });
    }
    setEditing(false);
  };
  const alertColor = alertDays !== null && alertDays !== undefined
    ? (alertDays <= 0 ? C.pink : alertDays <= 7 ? C.yellow : C.green)
    : null;
  const alertText = alertDays !== null && alertDays !== undefined
    ? (alertDays <= 0 ? "ЗАМОВ ЗАРАЗ" : `замов через ${alertDays}д`)
    : null;
  if (editing) return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      <input autoFocus type="number" min="0" max="365" value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        style={{ ...S.input, width: 54, fontSize: 11, padding: "3px 6px" }} />
      <span onClick={save} style={{ cursor: "pointer", color: C.green, fontSize: 11, fontWeight: 700 }}>OK</span>
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, cursor: "pointer" }} onClick={() => { setVal(value ?? ""); setEditing(true); }}>
      {value ? (
        <span style={{ fontSize: 11, color: C.text2 }}>{value}д</span>
      ) : (
        <span style={{ fontSize: 10, color: C.text4 }}>+ ввести</span>
      )}
      {alertText && <span style={{ fontSize: 10, fontWeight: 700, color: alertColor }}>{alertText}</span>}
    </div>
  );
}

function SalesSparkline({ bars }) {
  if (!bars || bars.every(v => v === 0)) return null;
  const max = Math.max(...bars, 1);
  const W = 10, GAP = 3, BAR_H = 16, COUNT_H = 10, DATE_H = 9;
  const H = BAR_H + COUNT_H + DATE_H;
  const today = new Date();
  return (
    <svg width={7 * W + 6 * GAP} height={H} style={{ display: "block", marginTop: 2 }}>
      {bars.map((v, i) => {
        const d = new Date(today);
        d.setDate(today.getDate() - (6 - i));
        const dayNum = d.getDate();
        const isToday = i === 6;
        const bh = Math.max(v > 0 ? 2 : 1, Math.round((v / max) * BAR_H));
        const x = i * (W + GAP);
        return (
          <g key={i}>
            <rect x={x} y={BAR_H - bh} width={W} height={bh}
              rx={2} fill={isToday ? C.blue : C.blue} opacity={v > 0 ? (isToday ? 0.9 : 0.6) : 0.12} />
            <text x={x + W / 2} y={BAR_H + COUNT_H} textAnchor="middle"
              fontSize={8} fill={v > 0 ? C.text2 : C.text4} fontFamily="monospace">
              {v > 0 ? v : "·"}
            </text>
            <text x={x + W / 2} y={H} textAnchor="middle"
              fontSize={8} fill={isToday ? C.text2 : C.text4}>
              {dayNum}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function DualStockCell({ ffStock, fbsStock, ownStock, tracking, isFf, availabilityName }) {
  // ffStock === null means FF but not yet catalog-synced (stock unknown)
  const ffVal = isFf ? (ffStock !== null && ffStock !== undefined ? ffStock : null) : null;
  const ffColor = ffVal === null ? C.text4 : ffVal > 0 ? C.green : C.pink;
  const fbsLabel = availabilityName === "Видалено з прайсу" ? "видалено"
    : availabilityName === "Немає в наявності" ? "0 шт."
    : fbsStock > 0 ? `${fbsStock} шт.` : null;
  const own = ownStock || 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 72 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <span style={{ fontSize: 9, color: C.purple, fontWeight: 700, width: 28, flexShrink: 0 }}>FF</span>
        <span style={{ fontWeight: 700, fontSize: 12, color: ffColor }}>
          {ffVal === null ? "—" : `${ffVal} шт.`}
        </span>
      </div>
      {tracking ? (
        <div style={{ display: "flex", alignItems: "center", gap: 5 }} title={`Прайс-лист: ${fbsLabel ?? "—"}`}>
          <span style={{ fontSize: 9, color: C.text4, fontWeight: 700, width: 28, flexShrink: 0 }}>Склад</span>
          <span style={{ fontWeight: 700, fontSize: 12, color: own > 0 ? C.green : C.pink }}>{own} шт.</span>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ fontSize: 9, color: C.text4, fontWeight: 700, width: 28, flexShrink: 0 }}>FBS</span>
          <span style={{ fontSize: 11, color: availabilityName === "Видалено з прайсу" ? C.text4 : C.text3 }}>
            {fbsLabel ?? "—"}
          </span>
        </div>
      )}
    </div>
  );
}

function PositionChart({ data, anchorRect, buybacks }) {
  if (!data || data.length < 2) return null;
  const W = 260, H = 150;
  const PAD = { top: 20, right: 16, bottom: 32, left: 36 };
  const cW = W - PAD.left - PAD.right;
  const cH = H - PAD.top - PAD.bottom;

  const vals = data.map(d => d.pos ?? 181);
  const rawMin = Math.min(...vals), rawMax = Math.max(...vals);
  const pad = Math.max(Math.round((rawMax - rawMin) * 0.2), 2);
  const minV = Math.max(1, rawMin - pad), maxV = rawMax + pad;
  const range = maxV - minV || 1;

  const px = i => PAD.left + (i / (vals.length - 1)) * cW;
  // Invert Y: lower position (better) = higher on chart
  const py = v => PAD.top + ((v - minV) / range) * cH;

  const pts = vals.map((v, i) => `${px(i)},${py(v)}`).join(" ");
  const first = vals[0], last = vals[vals.length - 1];
  const lineColor = last < first ? "#22c55e" : last > first ? "#f87171" : "#94a3b8";

  const tickCount = 3;
  const yTicks = Array.from({ length: tickCount + 1 }, (_, i) =>
    Math.round(minV + (i / tickCount) * (maxV - minV))
  );

  // Buyback set for O(1) lookup
  const buybackSet = new Set(buybacks || []);

  let top, left;
  if (anchorRect) {
    const popH = H + 36;
    left = Math.min(anchorRect.left - 10, window.innerWidth - W - 8);
    left = Math.max(8, left);
    top = anchorRect.top - popH - 6 > 0 ? anchorRect.top - popH - 6 : anchorRect.bottom + 6;
  }

  return createPortal(
    <div style={{
      position: "fixed", top, left,
      width: W, background: "#0f172a", border: "1px solid #1e293b",
      borderRadius: 10, padding: "0 0 4px",
      boxShadow: "0 12px 32px rgba(0,0,0,0.7)",
      zIndex: 9999, pointerEvents: "none",
    }}>
      {/* Header */}
      <div style={{ padding: "8px 12px 4px", borderBottom: "1px solid #1e293b", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.5px" }}>ПОЗИЦІЯ В КАТЕГОРІЇ</span>
        <span style={{ fontSize: 10, color: lineColor, fontWeight: 700 }}>
          {last >= 181 ? "не знайд." : `#${last}`}
          {first !== last && <span style={{ color: "#64748b", fontWeight: 400 }}> (було #{first >= 181 ? "?" : first})</span>}
        </span>
      </div>
      <svg width={W} height={H}>
        {/* Grid lines + Y labels */}
        {yTicks.map(tick => {
          const yy = py(tick);
          return (
            <g key={tick}>
              <line x1={PAD.left} x2={W - PAD.right} y1={yy} y2={yy}
                stroke="#1e293b" strokeWidth={1} strokeDasharray="4,4" />
              <text x={PAD.left - 6} y={yy + 4} textAnchor="end"
                fontSize={9} fill="#475569">{tick}</text>
            </g>
          );
        })}

        {/* "краще" label */}
        <text x={PAD.left} y={PAD.top - 6} fontSize={8} fill="#475569">↑ краще</text>

        {/* Buyback vertical markers */}
        {data.map((d, i) => {
          if (!buybackSet.has(d.date)) return null;
          const x = px(i);
          return (
            <g key={`bb-${i}`}>
              <line x1={x} x2={x} y1={PAD.top} y2={PAD.top + cH}
                stroke="#f59e0b" strokeWidth={1} strokeDasharray="3,3" opacity={0.7} />
              <text x={x} y={PAD.top + cH + 12} textAnchor="middle"
                fontSize={8} fill="#f59e0b">🛒</text>
            </g>
          );
        })}

        {/* Line */}
        <polyline points={pts} fill="none" stroke={lineColor} strokeWidth={2}
          strokeLinejoin="round" strokeLinecap="round" />

        {/* Data points + value labels */}
        {vals.map((v, i) => {
          const cx = px(i), cy = py(v);
          const label = v >= 181 ? "?" : String(v);
          const isLast = i === vals.length - 1;
          const showLabel = i === 0 || isLast || vals.length <= 8;
          return (
            <g key={i}>
              <circle cx={cx} cy={cy} r={isLast ? 4 : 2.5}
                fill={isLast ? lineColor : "#0f172a"} stroke={lineColor} strokeWidth={1.5} />
              {showLabel && (
                <text x={cx} y={cy - 7} textAnchor="middle"
                  fontSize={9} fill={isLast ? lineColor : "#94a3b8"} fontWeight={isLast ? 700 : 400}>
                  {label}
                </text>
              )}
            </g>
          );
        })}

        {/* X axis date labels */}
        {data.map((d, i) => {
          const total = data.length;
          const show = i === 0 || i === total - 1 || total <= 7 ||
            (total > 7 && (i === Math.floor(total / 3) || i === Math.floor(2 * total / 3)));
          if (!show) return null;
          return (
            <text key={i} x={px(i)} y={H - 6} textAnchor="middle"
              fontSize={8} fill="#475569">{d.date}</text>
          );
        })}
      </svg>
      {/* Buyback legend */}
      {(buybacks?.length > 0) && (
        <div style={{ padding: "2px 12px 6px", fontSize: 9, color: "#f59e0b", display: "flex", alignItems: "center", gap: 4 }}>
          <span>🛒</span><span>дата виkупу</span>
        </div>
      )}
    </div>,
    document.body
  );
}

function PositionSparkline({ data, buybacks }) {
  const [hover, setHover] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const ref = useRef(null);

  const onEnter = useCallback(() => {
    if (ref.current) setAnchorRect(ref.current.getBoundingClientRect());
    setHover(true);
  }, []);
  const onLeave = useCallback(() => setHover(false), []);

  if (!data || data.length < 2) return null;
  const vals = data.map(d => d.pos ?? 181);
  const first = vals[0], last = vals[vals.length - 1];
  const color = last < first ? "#22c55e" : last > first ? "#f87171" : "#94a3b8";

  // Tiny 44×14 inline indicator: just a line showing the trend direction
  const W = 44, H = 14;
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const px = i => (i / (vals.length - 1)) * W;
  const py = v => 1 + ((v - min) / range) * (H - 2);
  const pts = vals.map((v, i) => `${px(i)},${py(v)}`).join(" ");

  return (
    <span ref={ref} onMouseEnter={onEnter} onMouseLeave={onLeave}
      style={{ display: "inline-block", cursor: "pointer", marginTop: 3 }}>
      <svg width={W} height={H} style={{ display: "block" }}>
        <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5}
          strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={px(vals.length - 1)} cy={py(last)} r={2} fill={color} />
      </svg>
      {hover && <PositionChart data={data} anchorRect={anchorRect} buybacks={buybacks} />}
    </span>
  );
}

function RzStatusCell({ sectionName, availabilityName }) {
  const sColor = sectionName === "Активний" ? C.green
    : sectionName === "Архівний" ? C.text4
    : sectionName === "Заблокований" ? C.pink
    : sectionName === "Новий" ? C.blue : C.text4;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {sectionName && (
        <span style={{ fontSize: 10, color: sColor, fontWeight: 600 }}>{sectionName}</span>
      )}
      {availabilityName && (
        <span style={{ fontSize: 10, color: C.text4 }}>{availabilityName}</span>
      )}
    </div>
  );
}

function ProblemBadges({ p }) {
  const badges = [];
  const hasStock = (p.ff_stock >= 1) || (p.fbs_stock >= 1);
  const isDead = hasStock && p.status !== "inactive" &&
    (p.days_without_sale === null || p.days_without_sale === undefined || p.days_without_sale >= 7);

  if (isDead) {
    const days = p.days_without_sale;
    const daysLabel = days != null ? ` · ${days}д` : " · ніколи не продавався";
    badges.push({ key: "dead", color: C.yellow, label: `💤 мертвий запас${daysLabel}` });
  }
  if (!p.is_ff && p.ff_stock > 0) {
    badges.push({ key: "bypass", color: C.yellow, label: `⚡ FF байпас (${p.ff_stock} шт.)` });
  }
  if (badges.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 3 }}>
      {badges.map(b => (
        <span key={b.key} style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 4,
          background: `${b.color}18`, color: b.color, border: `1px solid ${b.color}30`, whiteSpace: "nowrap" }}>
          {b.label}
        </span>
      ))}
    </div>
  );
}

const COL_DEFS = [
  { id: 'photo',       header: 'Фото',       tip: null,              sortKey: null,            mobileHide: false },
  { id: 'status',      header: 'Статус',     tip: "Активний — є в продажу та є в наявності\nНемає в наявності — активний але 0 залишок\nАрхівний — видалено або деактивовано на Rozetka", sortKey: 'status', mobileHide: true },
  { id: 'ff',          header: 'FF/FBS',     tip: "FF — Rozetka зберігає та доставляє. FBS — ви відправляєте самі.", sortKey: null, mobileHide: true },
  { id: 'abc',         header: 'Кл',         tip: "ABC-клас:\nA — топ 80% виручки (пріоритет)\nB — наступні 15%\nC — решта 5%", sortKey: 'abc_class', mobileHide: true },
  { id: 'position',    header: 'Поз.',       tip: "Позиція товару в категорії Rozetka.\n↑ зелений — покращилась (менший номер)\n↓ червоний — погіршилась\n→ сірий — без змін\nне знайд. — не в перших 5 сторінках\n— немає даних (запусти парсер)", sortKey: 'position', mobileHide: true },
  { id: 'name',        header: 'Товар',      tip: "Клік на назву — відкриє товар на Rozetka", sortKey: 'name', mobileHide: false },
  { id: 'id',          header: 'ID',         tip: "Внутрішній ID товару на Rozetka",           sortKey: 'product_id', mobileHide: false },
  { id: 'article',     header: 'Арт.',       tip: "Артикул (SKU) товару",                      sortKey: null, mobileHide: false },
  { id: 'revenue',     header: 'Виручка',    tip: "Загальна сума продажів (ціна × кількість)", sortKey: 'revenue', mobileHide: false },
  { id: 'share',       header: 'Частка',     tip: "Відсоток від загальної виручки всіх товарів", sortKey: 'share_pct', mobileHide: true },
  { id: 'qty',         header: 'Продажі',    tip: "Всього — к-сть за весь час (успішні замовлення).\nСпарклайн — динаміка за 7 днів (всі замовлення крім скасованих, щоб бачити тренд без затримки).\n30д — к-сть за останні 30 днів:\n🟢 ≥1 шт/день · 🟡 ≥0.3/день · 🔴 мало", sortKey: 'quantity', mobileHide: false },
  { id: 'price',       header: 'Ціна',       tip: "Поточна ціна товару на Rozetka (з каталогу).\nСер. — середня фактична ціна продажу за обраний період (враховує промо).\n⬇ промо — товар продавався зі знижкою.", sortKey: 'catalog_price', mobileHide: false },
  { id: 'promo',       header: 'Промо',      tip: "Кількість одиниць, проданих за промо-ціною", sortKey: 'promo_count', mobileHide: true, hidden: true },
  { id: 'commission',  header: 'Комісія',    tip: "Комісія Rozetka у % від ціни (12–25% залежно від категорії)", sortKey: 'commission_pct', mobileHide: true },
  { id: 'cost',        header: 'Собівартість', tip: "Введіть собівартість товару (скільки ти заплатив постачальнику).\nКлік OK — зберегти.\n\nДоставка підставляється автоматично:\n• FF товари = 42 ₴ (пакування 15 + доставка 27.48, тариф Rozetka)\n• FBS товари = 49 ₴ (партнерська доставка, тариф Rozetka)", sortKey: null, mobileHide: true },
  { id: 'profit',      header: 'Прибуток/шт', tip: "Прибуток з одного продажу в грн та % маржі.\nФормула: Поточна ціна − Комісія − 3% платіжки − Доставка − Собівартість\nМаленький рядок: Маржа% · Загальний прибуток (по факту продажів)", sortKey: 'profit_per_unit', mobileHide: false },
  { id: 'margin',      header: 'Маржа',      tip: "Маржа = Прибуток ÷ Ціна × 100%\nПоказує рентабельність товару.", sortKey: 'margin_pct', mobileHide: true },
  { id: 'stock',       header: 'Залишок',    tip: "FF — реальний залишок на складі Rozetka.\nFBS — значення з прайс-листа (умовне).", sortKey: 'stock', mobileHide: false },
  { id: 'days_to_zero',header: 'Дні до нуля',tip: "Скільки днів залишилось поки товар закінчиться на FF складі.\n\nРозраховується: FF залишок ÷ темп продажів.\nТемп — якщо ≥3 шт. за 7 днів → тижневий темп, інакше → місячний темп (30 днів).\n\n🔴 ≤7 днів — критично, треба вже відправляти\n🟡 ≤30 днів — скоро закінчиться, плануй відправку\n\nПорожньо — якщо товар не продавався або запасу немає.", sortKey: 'days_to_zero', mobileHide: true },
  { id: 'rz_status',   header: 'Статус RZ',  tip: "Статус товару на Rozetka:\nАктивний — пройшов модерацію\nНовий — ще не модерований\nАрхівний — видалено або деактивовано\nЗаблокований — не пройшов модерацію", sortKey: 'section_name', mobileHide: true },
  { id: 'buybacks',    header: 'Виkупи',     tip: "Кількість самовиkупів товару (замовлення з власного складу).\nПоказує загальну кількість та дату останнього виkупу.", sortKey: 'buyback_count', mobileHide: true },
  { id: 'reviews',     header: 'Відгуки',    tip: "Загальна оцінка товару та оцінка останнього відгуку.\nЧервоний — останній відгук ≤3★ або без зірок (заблокований): потрібно перекрити.", sortKey: 'reviews_score', mobileHide: true },
  { id: 'cancellation', header: 'Скасув.',   tip: "% скасованих замовлень за останні 90 днів.\n🔴 >15% — висока відмова (проблема з описом / ціною / доставкою)\n🟡 >8% — помірна відмова", sortKey: 'cancellation_rate', mobileHide: true },
  { id: 'returns',      header: 'Поверн.',   tip: "% повернень: успішні замовлення, що пізніше стали поверненнями.\nВраховує лише 'пізні' повернення (успішний → скасований після виконання).\n🔴 >5% — висока частота повернень", sortKey: 'return_rate_pct', mobileHide: true },
  { id: 'min_promo',   header: 'Мін.промо', tip: "Мінімальна ціна при якій промо ще прибуткове.\nФормула: (собівартість + доставка + 10₴) ÷ (1 − комісія% − 3%)\nЗелений — promo_required_price прибуткова. Червоний — ціна від Rozetka нижче мінімуму.", sortKey: null, mobileHide: true },
  { id: 'lead_time',   header: 'Поставка',  tip: "Термін постачання від замовлення до отримання (днів).\nВведіть вручну. Використовується для розрахунку 'коли замовляти'.\n🔴 ЗАМОВ ЗАРАЗ — залишок < терміну поставки\n🟡 Замов скоро — залишок < 2× терміну", sortKey: null, mobileHide: true },
];
const DEFAULT_COL_ORDER = COL_DEFS.map(c => c.id);

export default function ProductsPage({
  allProducts, allProductsLoading,
  statusFilter, setStatusFilter,
  abcFilter, setAbcFilter,
  prodSearch, setProdSearch,
  prodPage, setProdPage,
  prodPerPage, setProdPerPage,
  sortBy, setSortBy,
  sortDir, setSortDir,
  localCosts, setLocalCosts,
  isMobile,
  criticalStockOnly, setCriticalStockOnly,
  problemsOnly, setProblemsOnly,
  realDead,
  reviewsMap,
  problemFilter = "all", setProblemFilter,
  pinnedIds = [], togglePin,
  onTaskCreate,
  tasksMap = {},
}) {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [groupFilter, setGroupFilter] = useState(null);
  const [noCostOnly, setNoCostOnly] = useState(false);
  const [favoriteOnly, setFavoriteOnly] = useState(false);
  const [fulfillmentFilter, setFulfillmentFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [catSearch, setCatSearch] = useState("");
  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef(null);
  const [importStatus, setImportStatus] = useState(null);
  const [posHistory, setPosHistory] = useState({});
  const [posBuybacks, setPosBuybacks] = useState({});
  const [sparklines, setSparklines] = useState({});
  const [historyProduct, setHistoryProduct] = useState(null);
  const fileRef = useRef();
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkCostVal, setBulkCostVal] = useState("");

  // Drag-and-drop column reordering
  const [colOrder, setColOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('products_col_order') || '');
      if (!Array.isArray(saved)) return DEFAULT_COL_ORDER;
      const allIds = new Set(DEFAULT_COL_ORDER);
      const valid = saved.filter(id => allIds.has(id));
      const missing = DEFAULT_COL_ORDER.filter(id => !valid.includes(id));
      return [...valid, ...missing];
    } catch { return DEFAULT_COL_ORDER; }
  });
  const [dragOver, setDragOver] = useState(null);
  const dragColRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('products_col_order', JSON.stringify(colOrder));
  }, [colOrder]);

  const orderedCols = colOrder.map(id => COL_DEFS.find(c => c.id === id)).filter(Boolean).filter(c => !c.hidden);

  const submitBulkCost = async () => {
    const cost = parseFloat(bulkCostVal);
    if (isNaN(cost) || cost < 0) return;
    const tkn = localStorage.getItem("crm_token");
    const items = Array.from(selectedIds).map(id => ({ rozetka_id: id, cost_price: cost }));
    await fetch(`${BASE}/api/products/cost-import`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + tkn },
      body: JSON.stringify(items),
    });
    const updates = {};
    items.forEach(i => { updates[i.rozetka_id] = cost; });
    setLocalCosts(prev => ({ ...prev, ...updates }));
    setSelectedIds(new Set());
    setSelectMode(false);
    setBulkModal(null);
    setBulkCostVal("");
  };

  useEffect(() => {
    const tkn = localStorage.getItem("crm_token");
    fetch(`${BASE}/api/products/positions/history`, {
      headers: { Authorization: "Bearer " + tkn }
    }).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.history) setPosHistory(d.history);
      if (d?.buybacks) setPosBuybacks(d.buybacks);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const tkn = localStorage.getItem("crm_token");
    fetch(`${BASE}/api/products/sparklines`, {
      headers: { Authorization: "Bearer " + tkn }
    }).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.sparklines) setSparklines(d.sparklines);
    }).catch(() => {});
  }, []);

  const exportCSV = () => {
    const rows = [["rozetka_id","article","name","cost_price","delivery_cost"]];
    (allProducts?.products || []).forEach(p => {
      rows.push([p.product_id, p.article || "", `"${(p.name||"").replace(/"/g,'""')}"`, p.cost_price || 0, p.delivery_cost_used || (p.is_ff ? 42 : 49)]);
    });
    const csv = rows.map(r => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    a.download = "cost_prices.csv";
    a.click();
  };

  const importCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    setImportStatus("reading");
    const text = await file.text();
    const lines = text.trim().split("\n").slice(1); // skip header
    const items = [];
    for (const line of lines) {
      const cols = line.split(",");
      const id = parseInt(cols[0]);
      const cost = parseFloat(cols[3]);
      const delivery = parseFloat(cols[4]);
      if (id && !isNaN(cost)) items.push({ rozetka_id: id, cost_price: cost, delivery_cost: isNaN(delivery) ? 49 : delivery });
    }
    if (!items.length) { setImportStatus("empty"); setTimeout(() => setImportStatus(null), 3000); return; }
    setImportStatus("saving");
    const tkn = localStorage.getItem("crm_token");
    const res = await fetch(`${BASE}/api/products/cost-import`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + tkn },
      body: JSON.stringify(items),
    });
    const data = await res.json();
    setImportStatus(`ok:${data.updated}`);
    // Update localCosts so UI reflects immediately
    const newCosts = { ...localCosts };
    items.forEach(i => { newCosts[i.rozetka_id] = { cost: i.cost_price, delivery: i.delivery_cost }; });
    setLocalCosts(newCosts);
    setTimeout(() => setImportStatus(null), 4000);
  };


  const getMinPrice = (p) => {
    const cost = localCosts[p.product_id]?.cost ?? p.cost_price;
    const extra = localCosts[p.product_id]?.extra ?? p.extra_cost ?? 0;
    const deliv = localCosts[p.product_id]?.delivery ?? p.delivery_cost_used ?? (p.is_ff ? 42 : 49);
    if (!cost) return null;
    const deduct = 1 - (p.commission_pct || 0) / 100 - 0.03;
    if (deduct <= 0) return null;
    return Math.ceil((cost + extra + deliv) / deduct);
  };

  // Close category dropdown on outside click
  useEffect(() => {
    if (!catOpen) return;
    const handler = (e) => { if (catRef.current && !catRef.current.contains(e.target)) setCatOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [catOpen]);

  const prodList = allProducts?.products || [];

  // Sorted unique categories
  const allCategories = [...new Set(prodList.map(p => p.category_name).filter(Boolean))].sort((a, b) => a.localeCompare(b, "uk"));

  const filtAbc = prodList
    .filter(p => {
      if (statusFilter === "all") return p.status !== "inactive";
      return p.status === statusFilter;
    })
    .filter(p => abcFilter === "all" || p.abc_class === abcFilter)
    .filter(p => {
      if (!prodSearch) return true;
      const s = prodSearch.toLowerCase();
      return p.name.toLowerCase().includes(s) ||
        (p.article || "").toLowerCase().includes(s) ||
        String(p.product_id).includes(s);
    })
    .filter(p => {
      if (!noCostOnly) return true;
      const cost = localCosts[p.product_id]?.cost ?? p.cost_price;
      return !cost || cost === 0;
    })
    .filter(p => {
      if (!criticalStockOnly) return true;
      const days = p.days_to_zero;
      return days !== null && days !== undefined && days > 0 && days < 14;
    })
    .filter(p => {
      if (!problemsOnly) return true;
      const isDeadStock = ((p.ff_stock >= 1) || (p.fbs_stock >= 1)) && p.status !== "inactive" && (p.days_without_sale === null || p.days_without_sale === undefined || p.days_without_sale >= 7);
      const isFFBypass = !p.is_ff && p.ff_stock > 0;
      return isDeadStock || isFFBypass;
    })
    .filter(p => {
      if (!problemFilter || problemFilter.size === 0) return true;
      const checks = {
        out_of_stock_ff: () => p.is_ff && (p.ff_stock || 0) === 0 && p.status !== "inactive",
        blocked:         () => p.upload_status === 11,
        loss:            () => !!p.is_loss,
        dead:            () => { const hasStock = (p.ff_stock >= 1) || (p.fbs_stock >= 1); return hasStock && p.status !== "inactive" && (p.days_without_sale === null || p.days_without_sale === undefined || p.days_without_sale >= 7); },
        ff_bypass:       () => !p.is_ff && (p.ff_stock || 0) > 0,
        neg_reviews:     () => { const r = reviewsMap?.[p.rozetka_id]; return p.status !== "inactive" && !!r && r.top_rating !== null && r.top_rating <= 3; },
        not_promo:       () => (p.promo_required_price || 0) > 0 && ((p.catalog_promo_price || 0) === 0 || (p.catalog_promo_price || 0) > p.promo_required_price),
      };
      return [...problemFilter].every(f => checks[f] ? checks[f]() : true);
    })
    .filter(p => {
      if (fulfillmentFilter === "all") return true;
      return fulfillmentFilter === "ff" ? p.is_ff : !p.is_ff;
    })
    .filter(p => categoryFilter === "all" || p.category_name === categoryFilter)
    .filter(p => !groupFilter || p.rz_group_id === groupFilter)
    .filter(p => !favoriteOnly || pinnedIds.includes(p.product_id))
    .slice().sort((a, b) => {
      let av, bv;
      if (sortBy === "min_price") {
        av = getMinPrice(a); bv = getMinPrice(b);
      } else if (sortBy === "reviews_score") {
        const ra = reviewsMap?.[a.rozetka_id], rb = reviewsMap?.[b.rozetka_id];
        av = ra == null ? 999 : (ra.top_rating == null ? -1 : ra.top_rating);
        bv = rb == null ? 999 : (rb.top_rating == null ? -1 : rb.top_rating);
      } else {
        av = a[sortBy]; bv = b[sortBy];
      }
      const aNull = av == null, bNull = bv == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === "string") return sortDir === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
      return sortDir === "desc" ? bv - av : av - bv;
    });
  const groupSizeMap = {};
  for (const p of prodList) {
    if (p.rz_group_id) groupSizeMap[p.rz_group_id] = (groupSizeMap[p.rz_group_id] || 0) + 1;
  }

  const prodTotalPages = Math.ceil(filtAbc.length / prodPerPage);
  const prodPageItems = filtAbc.slice((prodPage - 1) * prodPerPage, prodPage * prodPerPage);

  const doSort = (key) => {
    if (sortBy === key) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortBy(key); setSortDir(key === "reviews_score" ? "asc" : "desc"); }
    setProdPage(1);
  };
  const si = (key) => sortBy === key ? (sortDir === "desc" ? " ↓" : " ↑") : "";

  return (
    <div>
      {historyProduct && (
        <ProductHistoryModal
          productId={historyProduct.id}
          productName={historyProduct.name}
          productImage={historyProduct.image}
          onClose={() => setHistoryProduct(null)}
        />
      )}
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>
            Інвентар · {fmt(prodPageItems.length)} SKU
          </div>
          <div style={{ fontSize: 12, color: C.text3 }}>
            {filtAbc.length !== prodPageItems.length && `${fmt(filtAbc.length)} відфільтровано · `}
            всього {fmt(allProducts?.total ?? 0)}
            {allProductsLoading && " · Завантаження..."}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div onClick={() => { setSelectMode(v => !v); setSelectedIds(new Set()); }}
            style={{ fontSize: 12, cursor: "pointer", padding: "5px 10px", borderRadius: 8,
              background: selectMode ? `${C.blue}22` : "transparent",
              border: `1px solid ${selectMode ? C.blue + "60" : "transparent"}`,
              color: selectMode ? C.blue : C.text3, userSelect: "none" }}>
            {selectMode ? "✕ Вийти" : "☑ Виділити"}
          </div>
          <div style={{ fontSize: 12, color: C.blue, cursor: "pointer", textDecoration: "underline dotted" }}
            title="Прибуток = Ціна - (Комісія% + 3%) - Доставка - Собівартість. FF = 42 грн, FBS = 49 грн (тарифи Rozetka).">
            ℹ Формула прибутку
          </div>
        </div>
      </div>

      {/* Filter toolbar */}
      <div style={{ ...S.card, padding: "12px 14px", marginBottom: 10 }}>
        {/* Row 1: Status + ABC + Search */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 2, background: C.bg1, padding: 3, borderRadius: 11, border: `1px solid ${C.border}` }}>
            {[
              ["Всі", "all"],
              ["Активні", "active"],
              ["Нема в наявн.", "out_of_stock"],
              ["Архівні", "inactive"],
            ].map(([l, v]) => (
              <div key={v} onClick={() => setStatusFilter(v)} style={{
                padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                cursor: "pointer", userSelect: "none", transition: "all .12s",
                background: statusFilter === v ? `${C.blue}22` : "transparent",
                color: statusFilter === v ? C.blue : C.text3,
              }}>{l}</div>
            ))}
          </div>
          <div style={{ width: 1, height: 24, background: C.border }} />
          <div style={{ display: "flex", gap: 2, background: C.bg1, padding: 3, borderRadius: 11, border: `1px solid ${C.border}` }}>
            {["all","A","B","C"].map(f => (
              <div key={f} onClick={() => setAbcFilter(f)} style={{
                padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                cursor: "pointer", userSelect: "none", transition: "all .12s",
                background: abcFilter === f ? `${C.blue}22` : "transparent",
                color: abcFilter === f ? C.blue : C.text3,
              }}>{f === "all" ? "Всі класи" : `Клас ${f}`}</div>
            ))}
          </div>
          <input placeholder="Пошук по назві, артикулу або ID..."
            value={prodSearch} onChange={e => setProdSearch(e.target.value)}
            style={{ ...S.input, width: 260 }} />
          {/* Category dropdown */}
          <div ref={catRef} style={{ position: "relative" }}>
            <div onClick={() => { setCatOpen(v => !v); setCatSearch(""); }} style={{
              ...S.input, padding: "6px 12px", cursor: "pointer", userSelect: "none",
              display: "flex", alignItems: "center", gap: 6, minWidth: 180,
              background: categoryFilter !== "all" ? `${C.blue}18` : undefined,
              border: `1px solid ${categoryFilter !== "all" ? C.blue + "60" : C.border}`,
              color: categoryFilter !== "all" ? C.blue : C.text2,
            }}>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12 }}>
                {categoryFilter === "all" ? "Всі категорії" : categoryFilter}
              </span>
              <span style={{ color: C.text4, fontSize: 10 }}>{catOpen ? "▲" : "▼"}</span>
            </div>
            {catOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 500,
                background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 10,
                boxShadow: "0 8px 24px rgba(0,0,0,0.5)", width: 260, maxHeight: 320, overflow: "hidden",
                display: "flex", flexDirection: "column",
              }}>
                <div style={{ padding: "8px 8px 4px" }}>
                  <input autoFocus placeholder="Пошук категорії..."
                    value={catSearch} onChange={e => setCatSearch(e.target.value)}
                    style={{ ...S.input, width: "100%", fontSize: 12, padding: "5px 10px", boxSizing: "border-box" }} />
                </div>
                <div style={{ overflowY: "auto", maxHeight: 260 }}>
                  {[{ label: "Всі категорії", value: "all" },
                    ...allCategories
                      .filter(c => !catSearch || c.toLowerCase().includes(catSearch.toLowerCase()))
                      .map(c => ({ label: c, value: c }))
                  ].map(({ label, value }) => (
                    <div key={value} onClick={() => { setCategoryFilter(value); setCatOpen(false); setProdPage(1); }}
                      style={{
                        padding: "7px 12px", fontSize: 12, cursor: "pointer", userSelect: "none",
                        background: categoryFilter === value ? `${C.blue}22` : "transparent",
                        color: categoryFilter === value ? C.blue : C.text2,
                        borderRadius: 6, margin: "1px 4px",
                      }}
                      onMouseEnter={e => { if (categoryFilter !== value) e.currentTarget.style.background = C.bg3; }}
                      onMouseLeave={e => { if (categoryFilter !== value) e.currentTarget.style.background = "transparent"; }}
                    >{label}</div>
                  ))}
                  {catSearch && allCategories.filter(c => c.toLowerCase().includes(catSearch.toLowerCase())).length === 0 && (
                    <div style={{ padding: "10px 12px", fontSize: 12, color: C.text4 }}>Нічого не знайдено</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Row 2: Problem filters */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: C.text4, fontWeight: 600, flexShrink: 0 }}>Проблеми:</span>
          {[
            { id: "out_of_stock_ff", label: "📦 Нема запасу FF", color: C.pink },
            { id: "blocked",         label: "🔒 Заблокований",   color: C.pink },
            { id: "loss",            label: "📉 В мінус",        color: C.pink },
            { id: "dead",            label: "💤 Мертвий запас",  color: C.yellow },
            { id: "ff_bypass",       label: "⚡ FF байпас",      color: "#fb923c" },
            { id: "neg_reviews",     label: "★ Негативні відгуки", color: "#f87171" },
            { id: "not_promo",       label: "📧 Не в промо",     color: C.blue },
          ].map(({ id, label, color }) => {
            const active = problemFilter?.has(id);
            return (
              <div key={id} onClick={() => { const next = new Set(problemFilter); active ? next.delete(id) : next.add(id); setProblemFilter(next); setProdPage(1); }} style={{
                padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                cursor: "pointer", userSelect: "none", transition: "all .12s",
                background: active ? `${color}22` : C.bg1,
                color: active ? color : C.text3,
                border: `1px solid ${active ? color + "60" : C.border}`,
              }}>{label}</div>
            );
          })}
        </div>
        {/* Row 3: Quick filters */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <div onClick={() => { setCriticalStockOnly(v => !v); setProdPage(1); }} style={{
            padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", userSelect: "none",
            background: criticalStockOnly ? `${C.pink}22` : C.bg1, color: criticalStockOnly ? C.pink : C.text3,
            border: `1px solid ${criticalStockOnly ? C.pink : C.border}`,
          }}>
            ⚡ Критичний запас &lt;14д {criticalStockOnly && `(${filtAbc.length})`}
          </div>
          <div onClick={() => { setNoCostOnly(v => !v); setProdPage(1); }} style={{
            padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", userSelect: "none",
            background: noCostOnly ? `${C.pink}22` : C.bg1, color: noCostOnly ? C.pink : C.text3,
            border: `1px solid ${noCostOnly ? C.pink : C.border}`,
          }}>
            ⚠ Без собівартості {noCostOnly && `(${filtAbc.length})`}
          </div>
          <div onClick={() => { setFavoriteOnly(v => !v); setProdPage(1); }} style={{
            padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", userSelect: "none",
            background: favoriteOnly ? `${C.yellow}22` : C.bg1, color: favoriteOnly ? C.yellow : C.text3,
            border: `1px solid ${favoriteOnly ? C.yellow + "80" : C.border}`,
          }}>
            ★ Обрані {favoriteOnly && `(${filtAbc.length})`}
          </div>
          <div style={{ width: 1, height: 24, background: C.border }} />
          <div style={{ display: "flex", gap: 2, background: C.bg1, padding: 3, borderRadius: 11, border: `1px solid ${C.border}` }}>
            {[["Всі", "all"], ["FF", "ff"], ["FBS", "fbs"]].map(([l, v]) => (
              <div key={v} onClick={() => { setFulfillmentFilter(v); setProdPage(1); }} style={{
                padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                cursor: "pointer", userSelect: "none", transition: "all .12s",
                background: fulfillmentFilter === v ? `${v === "ff" ? C.purple : C.green}22` : "transparent",
                color: fulfillmentFilter === v ? (v === "ff" ? C.purple : v === "fbs" ? C.green : C.blue) : C.text3,
              }}>{l}</div>
            ))}
          </div>
          {groupFilter && (
            <div onClick={() => { setGroupFilter(null); setProdPage(1); }} style={{
              padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", userSelect: "none",
              background: `${C.blue}22`, color: C.blue, border: `1px solid ${C.blue}60`,
            }}>
              ⬡ Група × скасувати
            </div>
          )}
        </div>
        {/* Row 4: Per page + pagination summary */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: C.text3 }}>На сторінці:</span>
          <div style={{ display: "flex", gap: 2, background: C.bg1, padding: 2, borderRadius: 9, border: `1px solid ${C.border}` }}>
            {[25, 50, 100, 200].map(n => (
              <div key={n} onClick={() => setProdPerPage(n)} style={{
                padding: "4px 10px", borderRadius: 7, fontSize: 12, fontWeight: 600,
                cursor: "pointer", userSelect: "none", transition: "all .12s",
                background: prodPerPage === n ? `${C.blue}22` : "transparent",
                color: prodPerPage === n ? C.blue : C.text3,
              }}>{n}</div>
            ))}
          </div>
          <span style={{ fontSize: 12, color: C.text4, marginLeft: 4 }}>
            {filtAbc.length > 0 && `${(prodPage - 1) * prodPerPage + 1}–${Math.min(prodPage * prodPerPage, filtAbc.length)} з ${fmt(filtAbc.length)}`}
          </span>
          {colOrder.join(',') !== DEFAULT_COL_ORDER.join(',') && (
            <div onClick={() => setColOrder(DEFAULT_COL_ORDER)} style={{
              marginLeft: 8, padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 600,
              cursor: "pointer", userSelect: "none", background: `${C.blue}18`,
              color: C.blue, border: `1px solid ${C.blue}40`,
            }} title="Скинути порядок колонок">
              ↺ колонки
            </div>
          )}
          <div style={{ marginLeft: "auto" }}>
            <div onClick={() => {
              const rows = [["ID", "Артикул", "Назва", "Категорія", "Ціна", "Залишок FF", "Залишок Склад", "Залишок FBS (прайс)", "FF?", "ABC", "Продажі шт.", "Виручка", "Прибуток/шт", "Маржа%", "Собівартість", "Дні до нуля", "Статус", "Скасув.%", "Поверн.%"]];
              filtAbc.forEach(p => rows.push([
                p.product_id, p.article || "", p.name || "", p.category_name || "",
                p.avg_price || 0, p.ff_stock || 0, p.own_stock || 0, p.fbs_stock || 0,
                p.is_ff ? "FF" : "FBS", p.abc_class || "",
                p.quantity || 0, p.revenue || 0,
                p.profit_per_unit || 0, p.margin_pct || 0,
                p.cost_price || 0, p.days_to_zero ?? "",
                p.section_name || "",
                p.cancellation_rate || 0, p.return_rate_pct || 0,
              ]));
              exportXlsx([{ name: "Товари", rows }], "товари.xlsx");
            }} style={{
              padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: "pointer", userSelect: "none",
              background: `${C.green}18`, color: C.green,
              border: `1px solid ${C.green}40`,
            }}>
              ↓ Excel ({filtAbc.length})
            </div>
          </div>
        </div>
      </div>
      {!allProducts && !allProductsLoading && (
        <div style={{ color: C.text3, padding: 40, textAlign: "center" }}>
          Завантаження каталогу...
        </div>
      )}
      {(allProducts || allProductsLoading) && (
      <div style={{ ...S.card, padding: 0 }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: C.bg1 }}>
                {selectMode && (
                  <th style={{ ...S.th(false), width: 36, padding: "0 10px" }}>
                    <input type="checkbox"
                      checked={prodPageItems.length > 0 && selectedIds.size === prodPageItems.length}
                      onChange={e => setSelectedIds(e.target.checked ? new Set(prodPageItems.map(p => p.product_id)) : new Set())}
                    />
                  </th>
                )}
                {orderedCols.map(col => {
                  if (col.mobileHide && isMobile) return null;
                  const isOver = dragOver === col.id;
                  return (
                    <th key={col.id}
                      draggable
                      onDragStart={e => { dragColRef.current = col.id; e.dataTransfer.effectAllowed = 'move'; }}
                      onDragOver={e => { e.preventDefault(); setDragOver(col.id); }}
                      onDragLeave={() => setDragOver(null)}
                      onDrop={e => {
                        e.preventDefault();
                        const fromId = dragColRef.current;
                        setDragOver(null);
                        if (!fromId || fromId === col.id) return;
                        setColOrder(prev => {
                          const next = [...prev];
                          const from = next.indexOf(fromId);
                          const to = next.indexOf(col.id);
                          next.splice(from, 1);
                          next.splice(to, 0, fromId);
                          return next;
                        });
                        dragColRef.current = null;
                      }}
                      onClick={() => col.sortKey && doSort(col.sortKey)}
                      style={{
                        ...S.th(!!col.sortKey),
                        cursor: 'grab',
                        borderLeft: isOver ? `2px solid ${C.blue}` : undefined,
                        background: isOver ? `${C.blue}10` : C.bg1,
                        userSelect: 'none',
                      }}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                        {col.header}{col.sortKey ? si(col.sortKey) : ""}
                        {col.tip && <InfoTip text={col.tip} />}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {prodPageItems.map((p) => {
                const cost = localCosts[p.product_id]?.cost ?? p.cost_price;
                const deliv = localCosts[p.product_id]?.delivery ?? p.delivery_cost_used ?? (p.is_ff ? 42 : 49);
                const extraCost = localCosts[p.product_id]?.extra ?? p.extra_cost ?? 0;
                const catalogPrice = p.catalog_price || 0;
                const catalogPromoPrice = p.catalog_promo_price || 0;
                const promoRequiredPrice = p.promo_required_price || 0;
                // isPromo: show promo badge only if there were actual promo sales in the selected period
                const isPromo = catalogPromoPrice > 0 && catalogPromoPrice < catalogPrice * 0.99;
                // promoRec: Rozetka has a required promo price (Необхідна промо ціна)
                const promoRec = promoRequiredPrice > 0;
                // Use current selling price: promo price if active, otherwise catalog price
                const effectivePrice = catalogPromoPrice > 0 && catalogPromoPrice < catalogPrice
                  ? catalogPromoPrice
                  : (catalogPrice > 0 ? catalogPrice : (p.avg_price || 0));
                const price = effectivePrice;
                const comm = price * p.commission_pct / 100;
                const payFee = price * 0.03;
                const profitPerUnit = price - comm - payFee - deliv - cost - extraCost;
                const marginPct = price > 0 ? profitPerUnit / price * 100 : 0;
                const isLoss = (cost > 0 || extraCost > 0) && profitPerUnit < 0;
                const isInactive = p.status === "inactive";
                const minPrice = getMinPrice(p);
                const comparePrice = p.catalog_price > 0 ? p.catalog_price : (p.avg_price || 0);
                const priceOk = minPrice !== null && comparePrice > 0 && comparePrice >= minPrice;
                const priceLow = minPrice !== null && comparePrice > 0 && comparePrice < minPrice;
                return (
                  <tr key={p.product_id}
                    onMouseEnter={() => setHoveredRow(p.product_id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      background: isInactive ? "rgba(52,64,90,.15)" : isLoss ? "rgba(244,114,182,.04)" : "transparent",
                      opacity: isInactive ? 0.6 : 1,
                    }}>
                    {selectMode && (
                      <td style={{ ...S.td, width: 36, padding: "0 10px" }}>
                        <input type="checkbox"
                          checked={selectedIds.has(p.product_id)}
                          onChange={e => setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(p.product_id); else next.delete(p.product_id);
                            return next;
                          })}
                        />
                      </td>
                    )}
                    {orderedCols.map(col => {
                      if (col.mobileHide && isMobile) return null;
                      switch (col.id) {
                        case 'photo': return <td key={col.id} style={S.td}><Img src={p.image_url} size={34} /></td>;
                        case 'status': return <td key={col.id} style={S.td}><ProductStatusBadge status={p.status} /></td>;
                        case 'ff': return <td key={col.id} style={S.td}>{p.is_ff !== undefined ? <FfBadge isFf={p.is_ff} /> : <span style={{color:C.text4}}>—</span>}</td>;
                        case 'abc': return <td key={col.id} style={S.td}><AbcBadge cls={p.abc_class} /></td>;
                        case 'position': return (
                          <td key={col.id} style={{ ...S.td, textAlign: "center", minWidth: 68 }}>
                            {p.position != null ? (
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}>
                                <span style={{ fontWeight: 700, fontSize: 13, color: C.text1 }}>{p.position}</span>
                                {p.position_prev != null && p.position !== p.position_prev && (
                                  <span style={{ fontSize: 11, color: p.position < p.position_prev ? "#22c55e" : "#f87171" }}>
                                    {p.position < p.position_prev ? `↑${p.position_prev - p.position}` : `↓${p.position - p.position_prev}`}
                                  </span>
                                )}
                                {p.position_prev != null && p.position === p.position_prev && (
                                  <span style={{ fontSize: 11, color: C.text4 }}>→</span>
                                )}
                                <PositionSparkline data={posHistory[String(p.product_id)]} buybacks={posBuybacks[String(p.product_id)]} />
                              </div>
                            ) : p.position_synced_at
                              ? <span style={{ color: C.text4, fontSize: 11 }} title={`Не знайдено в перших 5 сторінках. Оновлено: ${p.position_synced_at}`}>не знайд.</span>
                              : <span style={{ color: C.text4 }}>—</span>}
                          </td>
                        );
                        case 'name': return (
                          <td key={col.id} style={{ ...S.td, maxWidth: isMobile ? 160 : 210 }}>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
                              {isLoss && <span style={{ color: C.pink, flexShrink: 0, marginTop: 1 }}>⚠</span>}
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <ProdLink name={p.name} article={p.article} productId={p.product_id} style={{ fontSize: 12 }} noMenu />
                                {!isMobile && (
                                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                                    <ProblemBadges p={p} />
                                    {p.rz_group_id && (groupSizeMap[p.rz_group_id] || 0) > 1 && (
                                      <span
                                        onClick={e => { e.stopPropagation(); setGroupFilter(groupFilter === p.rz_group_id ? null : p.rz_group_id); setProdPage(1); }}
                                        title={groupFilter === p.rz_group_id ? "Скасувати фільтр групи" : `Показати всі ${groupSizeMap[p.rz_group_id]} варіанти`}
                                        style={{ cursor: "pointer", fontSize: 10, fontWeight: 700,
                                          color: groupFilter === p.rz_group_id ? C.yellow : C.blue,
                                          background: (groupFilter === p.rz_group_id ? C.yellow : C.blue) + "22",
                                          borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap" }}
                                      >
                                        ⬡ {groupSizeMap[p.rz_group_id]}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div style={{ marginLeft: 6, display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
                                opacity: (hoveredRow === p.product_id || tasksMap[p.product_id] > 0 || pinnedIds.includes(p.product_id)) ? 1 : 0,
                                transition: "opacity .15s",
                              }}>
                                {tasksMap[p.product_id] > 0 ? (
                                  <span onClick={e => { e.stopPropagation(); onTaskCreate && onTaskCreate(p); }}
                                    title={`${tasksMap[p.product_id]} задач(и) по товару`}
                                    style={{ cursor: "pointer", fontSize: 10, fontWeight: 700, color: C.yellow, whiteSpace: "nowrap" }}
                                  >☑ {tasksMap[p.product_id]}</span>
                                ) : onTaskCreate ? (
                                  <span onClick={e => { e.stopPropagation(); onTaskCreate(p); }}
                                    title="Створити задачу"
                                    style={{ cursor: "pointer", fontSize: 10, fontWeight: 600, color: C.text4, whiteSpace: "nowrap" }}
                                  >+ задача</span>
                                ) : null}
                                {togglePin && (
                                  <span onClick={e => { e.stopPropagation(); togglePin(p.product_id); }}
                                    title={pinnedIds.includes(p.product_id) ? "Прибрати з вітрини" : "Додати до вітрини"}
                                    style={{ cursor: "pointer", fontSize: 14, color: "#fbbf24" }}
                                  >★</span>
                                )}
                              </div>
                            </div>
                          </td>
                        );
                        case 'id': return <td key={col.id} style={{ ...S.td, color: C.blue, fontFamily: "monospace", fontSize: 11 }}>{p.product_id}</td>;
                        case 'article': return <td key={col.id} style={{ ...S.td, color: C.text4, fontFamily: "monospace", fontSize: 11 }}>{p.article}</td>;
                        case 'revenue': return <td key={col.id} style={{ ...S.td, color: C.text1, fontWeight: 700 }}>{p.revenue > 0 ? fmtUAH(p.revenue) : <span style={{color:C.text4}}>—</span>}</td>;
                        case 'share': return <td key={col.id} style={{ ...S.td, color: C.text2 }}>{p.share_pct > 0 ? p.share_pct + "%" : <span style={{color:C.text4}}>—</span>}</td>;
                        case 'qty': {
                          const total = p.quantity || 0;   // всього за весь час
                          const mQty = p.month_qty || 0;   // останні 30 днів
                          const dailyAvg = mQty / 30;
                          const mColor = mQty === 0 ? C.text4
                            : dailyAvg >= 1 ? C.green
                            : dailyAvg >= 0.3 ? C.yellow : C.pink;
                          const spark = sparklines[String(p.product_id)];
                          // show the cell if there are recent (incl. pending) orders in the sparkline,
                          // even when there are no realized (successful) sales yet
                          const noData = total === 0 && mQty === 0 && (!spark || spark.every(v => v === 0));
                          return (
                            <td key={col.id} style={{ ...S.td }}>
                              {noData ? <span style={{ color: C.text4 }}>—</span> : (
                                <div
                                  style={{ display: "flex", flexDirection: "column", gap: 2, cursor: "pointer" }}
                                  onClick={e => { e.stopPropagation(); setHistoryProduct({ id: p.product_id, name: p.name, image: p.image_url }); }}
                                  title="Відкрити повну историю товару"
                                >
                                  <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                                    <span style={{ color: C.text1, fontWeight: 800, fontSize: 14 }}>{total}</span>
                                    <span style={{ color: C.text4, fontSize: 9 }}>всього</span>
                                  </div>
                                  {spark && <SalesSparkline bars={spark} />}
                                  <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                                    <span style={{ color: mColor, fontWeight: 600, fontSize: 11 }}>{mQty}</span>
                                    <span style={{ color: C.text4, fontSize: 9 }}>за 30д</span>
                                  </div>
                                </div>
                              )}
                            </td>
                          );
                        }
                        case 'price': return (
                          <td key={col.id} style={{ ...S.td, color: C.text1 }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                              <span style={{ fontWeight: 700 }}>
                                {catalogPrice > 0 ? fmtUAH(catalogPrice) : p.avg_price > 0 ? fmtUAH(p.avg_price) : "—"}
                              </span>
                              {!isMobile && (isPromo || p.is_promo_sent === 2) && (
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  {(() => {
                                    const showOrange = p.is_promo_sent === 2 && !isPromo;
                                    const clr = showOrange ? "#fb923c" : C.yellow;
                                    const price = showOrange ? promoRequiredPrice : catalogPromoPrice;
                                    return price > 0 ? (
                                      <>
                                        <span title={showOrange ? `Rozetka активно просуває цей товар. Рекомендована ціна: ${fmtUAH(price)}` : undefined} style={{
                                          fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 4,
                                          background: `${clr}22`, color: clr, border: `1px solid ${clr}40`,
                                          cursor: showOrange ? "help" : "default",
                                        }}>🏷 ПРОМО</span>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: clr }}>{fmtUAH(price)}</span>
                                      </>
                                    ) : null;
                                  })()}
                                </div>
                              )}
                              {promoRec && !isMobile && (
                                <div
                                  title={`Необхідна промо ціна Rozetka: ${fmtUAH(promoRequiredPrice)}`}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: 3,
                                    fontSize: 10, fontWeight: 600, padding: "1px 5px", borderRadius: 4,
                                    background: `${C.blue}15`, color: C.blue, border: `1px solid ${C.blue}30`,
                                    cursor: "help",
                                  }}
                                >
                                  📋 рек. {fmtUAH(promoRequiredPrice)}
                                </div>
                              )}
                            </div>
                          </td>
                        );
                        case 'promo': return <td key={col.id} style={S.td}>{p.promo_count > 0 ? <Badge color={C.pink}>{p.promo_count} шт.</Badge> : <span style={{ color: C.text4 }}>-</span>}</td>;
                        case 'commission': return <td key={col.id} style={{ ...S.td, color: p.commission_pct > 0 ? C.yellow : C.text4 }}>{p.commission_pct > 0 ? p.commission_pct + "%" : "—"}</td>;
                        case 'cost': return (
                          <td key={col.id} style={S.td}>
                            <CostInput key={p.product_id} productId={p.product_id} cost={cost} delivery={deliv} extra={extraCost}
                              onSave={(c, d, ex) => setLocalCosts(prev => ({ ...prev, [p.product_id]: { cost: c, delivery: d, extra: ex } }))} />
                          </td>
                        );
                        case 'min_price': return (
                          <td key={col.id} style={S.td}>
                            {minPrice === null ? <span style={{ color: C.text4, fontSize: 11 }}>—</span> : (
                              <div style={{
                                display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 2,
                                background: priceLow ? `${C.pink}12` : priceOk ? `${C.green}10` : `${C.bg3}`,
                                border: `1px solid ${priceLow ? C.pink + "40" : priceOk ? C.green + "30" : C.border}`,
                                borderRadius: 8, padding: "4px 8px",
                              }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: priceLow ? C.pink : priceOk ? C.green : C.text2, fontVariantNumeric: "tabular-nums" }}>
                                  {priceLow ? "⚠ " : priceOk ? "✓ " : ""}{minPrice.toLocaleString()} ₴
                                </span>
                                {comparePrice > 0 && <span style={{ fontSize: 10, color: C.text4 }}>{p.catalog_price > 0 ? "ціна" : "сер."} {comparePrice.toLocaleString()} ₴</span>}
                              </div>
                            )}
                          </td>
                        );
                        case 'profit': {
                          const adSpend = p.ad_spend || 0;
                          const adCostPerOrder = adSpend > 0 && p.quantity > 0 ? adSpend / p.quantity : 0;
                          return (
                          <td key={col.id} style={S.td}>
                            {cost > 0 ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: isLoss ? C.pink : C.green }}>
                                  {profitPerUnit >= 0 ? "+" : ""}{Math.round(profitPerUnit)} ₴{isPromo && <span style={{ fontSize: 10, fontWeight: 600, color: C.yellow, marginLeft: 4 }}>від промо</span>}
                                </span>
                                <span style={{ fontSize: 10, color: isLoss ? C.pink : C.text3 }}>
                                  {marginPct.toFixed(1)}% · {(p.profit_total ?? 0) >= 0 ? "+" : ""}{Math.round(p.profit_total ?? 0)} ₴ всього
                                </span>
                                {adCostPerOrder > 0 && (
                                  <span
                                    title={`Реклама: ${Math.round(adSpend)} ₴ витрат / ${p.quantity} зам. = ${adCostPerOrder.toFixed(1)} ₴/шт (amortized)`}
                                    style={{ fontSize: 10, color: C.yellow, cursor: "help" }}
                                  >
                                    📢 -{adCostPerOrder.toFixed(1)} ₴/шт
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <span style={{ color: C.text4, fontSize: 12 }}>немає собів.</span>
                                {adCostPerOrder > 0 && (
                                  <span title={`Реклама: ${Math.round(adSpend)} ₴ / ${p.quantity} зам.`} style={{ fontSize: 10, color: C.yellow, cursor: "help" }}>
                                    📢 -{adCostPerOrder.toFixed(1)} ₴/шт
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          );
                        }
                        case 'margin': return <td key={col.id} style={{ ...S.td, color: isLoss ? C.pink : C.green }}>{cost > 0 ? marginPct.toFixed(1) + "%" : "-"}</td>;
                        case 'stock': return (
                          <td key={col.id} style={S.td}>
                            <DualStockCell ffStock={p.ff_stock ?? (p.is_ff ? p.stock : 0)} fbsStock={p.fbs_stock ?? (!p.is_ff ? p.stock : 0)} ownStock={p.own_stock} tracking={allProducts?.warehouse_tracking} isFf={p.is_ff} availabilityName={p.availability_name} />
                            {p.out_of_stock_since && (() => {
                              const days = Math.floor((Date.now() - new Date(p.out_of_stock_since)) / 86400000);
                              return days > 0 ? <div style={{ fontSize: 10, color: C.pink, marginTop: 2 }} title={`Немає в наявності з ${p.out_of_stock_since.slice(0,10)}`}>OOS {days}д</div> : null;
                            })()}
                          </td>
                        );
                        case 'days_to_zero': return <td key={col.id} style={S.td}><DaysBadge days={p.days_to_zero} /></td>;
                        case 'rz_status': return <td key={col.id} style={S.td}><RzStatusCell sectionName={p.section_name} availabilityName={p.availability_name} /></td>;
                        case 'buybacks': {
                          if (!p.buyback_count) return <td key={col.id} style={{ ...S.td, color: C.text4 }}>—</td>;
                          const d = p.last_buyback_date ? new Date(p.last_buyback_date) : null;
                          const dateStr = d ? `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}` : "";
                          return (
                            <td key={col.id} style={S.td}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <span style={{ fontWeight: 700, color: C.blue, fontSize: 12 }}>{p.buyback_count} шт.</span>
                                {dateStr && <span style={{ fontSize: 10, color: C.text4 }}>ост. {dateStr}</span>}
                              </div>
                            </td>
                          );
                        }
                        case 'reviews': {
                          const rev = reviewsMap?.[p.rozetka_id];
                          if (!rev) return <td key={col.id} style={{ ...S.td, color: C.text4, fontSize: 11 }}>—</td>;
                          const topBad = rev.top_rating === null || rev.top_rating === undefined || rev.top_rating <= 3;
                          const stars = n => n ? '★'.repeat(n) + '☆'.repeat(5 - n) : null;
                          return (
                            <td key={col.id} style={{ ...S.td }}>
                              <div style={{ background: topBad ? "rgba(248,113,113,0.1)" : "transparent", borderRadius: 6, padding: "3px 6px", display: "inline-block" }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: topBad ? "#f87171" : C.text1 }}>
                                  ★ {rev.agg_rating?.toFixed(1) ?? "—"}
                                  <span style={{ fontSize: 10, fontWeight: 400, color: C.text4, marginLeft: 3 }}>({rev.review_count ?? 0})</span>
                                </div>
                                <div style={{ fontSize: 11, color: topBad ? "#f87171" : C.text3, fontWeight: topBad ? 700 : 400, marginTop: 1 }}>
                                  {rev.top_rating === null || rev.top_rating === undefined
                                    ? "⚠ без зірок"
                                    : stars(rev.top_rating)}
                                </div>
                              </div>
                            </td>
                          );
                        }
                        case 'cancellation': {
                          const cr = p.cancellation_rate || 0;
                          if (cr === 0) return <td key={col.id} style={{ ...S.td, color: C.text4 }}>—</td>;
                          const color = cr > 15 ? C.pink : cr > 8 ? C.yellow : C.text3;
                          return (
                            <td key={col.id} style={S.td}>
                              <span style={{ fontWeight: 700, color, fontSize: 12 }}>{cr}%</span>
                            </td>
                          );
                        }
                        case 'returns': {
                          const rr = p.return_rate_pct || 0;
                          if (rr === 0) return <td key={col.id} style={{ ...S.td, color: C.text4 }}>—</td>;
                          const color = rr > 5 ? C.pink : rr > 2 ? C.yellow : C.text3;
                          return (
                            <td key={col.id} style={S.td}>
                              <span style={{ fontWeight: 700, color, fontSize: 12 }} title={p.avg_return_days ? `Середній термін: ${p.avg_return_days} дн.` : ""}>
                                {rr}%
                              </span>
                            </td>
                          );
                        }
                        case 'min_promo': {
                          const minP = p.min_promo_price;
                          const reqP = p.promo_required_price;
                          if (!minP) return <td key={col.id} style={{ ...S.td, color: C.text4 }}>—</td>;
                          const profitable = !reqP || reqP >= minP;
                          return (
                            <td key={col.id} style={S.td}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <span style={{ fontWeight: 700, color: profitable ? C.green : C.pink, fontSize: 12 }}>{Math.round(minP)} ₴</span>
                                {reqP > 0 && <span style={{ fontSize: 10, color: C.text4 }}>RZ: {Math.round(reqP)} ₴</span>}
                              </div>
                            </td>
                          );
                        }
                        case 'lead_time': {
                          const lt = p.lead_time_days;
                          const alert = p.reorder_alert_days;
                          return (
                            <td key={col.id} style={S.td}>
                              <LeadTimeCell
                                rozetkaId={p.product_id}
                                value={lt}
                                alertDays={alert}
                                token={localStorage.getItem("crm_token")}
                              />
                            </td>
                          );
                        }
                        default: return null;
                      }
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Pagination */}
      {prodTotalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 16, flexWrap: "wrap" }}>
          <button onClick={() => setProdPage(1)} disabled={prodPage === 1}
            style={{ padding: "6px 10px", borderRadius: 8, background: C.bg2, border: `1px solid ${C.border}`,
              color: prodPage === 1 ? C.text4 : C.blue, cursor: prodPage === 1 ? "default" : "pointer", fontSize: 12 }}>«</button>
          <button onClick={() => setProdPage(p => Math.max(1, p - 1))} disabled={prodPage === 1}
            style={{ padding: "6px 14px", borderRadius: 8, background: C.bg2, border: `1px solid ${C.border}`,
              color: prodPage === 1 ? C.text4 : C.blue, cursor: prodPage === 1 ? "default" : "pointer", fontSize: 12 }}>← Назад</button>

          {/* Page number buttons */}
          {Array.from({ length: Math.min(7, prodTotalPages) }, (_, i) => {
            let p;
            if (prodTotalPages <= 7) p = i + 1;
            else if (prodPage <= 4) p = i + 1;
            else if (prodPage >= prodTotalPages - 3) p = prodTotalPages - 6 + i;
            else p = prodPage - 3 + i;
            return (
              <button key={p} onClick={() => setProdPage(p)}
                style={{ padding: "6px 10px", minWidth: 34, borderRadius: 8, fontSize: 12, fontWeight: 600,
                  background: prodPage === p ? C.blue : C.bg2,
                  border: `1px solid ${prodPage === p ? C.blue : C.border}`,
                  color: prodPage === p ? "#fff" : C.text2, cursor: "pointer" }}>{p}</button>
            );
          })}

          <button onClick={() => setProdPage(p => Math.min(prodTotalPages, p + 1))} disabled={prodPage === prodTotalPages}
            style={{ padding: "6px 14px", borderRadius: 8, background: C.bg2, border: `1px solid ${C.border}`,
              color: prodPage === prodTotalPages ? C.text4 : C.blue, cursor: prodPage === prodTotalPages ? "default" : "pointer", fontSize: 12 }}>Вперед →</button>
          <button onClick={() => setProdPage(prodTotalPages)} disabled={prodPage === prodTotalPages}
            style={{ padding: "6px 10px", borderRadius: 8, background: C.bg2, border: `1px solid ${C.border}`,
              color: prodPage === prodTotalPages ? C.text4 : C.blue, cursor: prodPage === prodTotalPages ? "default" : "pointer", fontSize: 12 }}>»</button>
          <span style={{ color: C.text3, fontSize: 12, marginLeft: 4 }}>
            сторінка {prodPage} / {prodTotalPages}
          </span>
        </div>
      )}

      {/* Bulk action bar */}
      {selectMode && selectedIds.size > 0 && createPortal(
        <div style={{
          position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
          zIndex: 9999, display: "flex", alignItems: "center", gap: 12,
          background: C.bg2, border: `1px solid ${C.blue}50`, borderRadius: 16,
          padding: "12px 20px", boxShadow: "0 8px 32px rgba(0,0,0,.6)",
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.text1 }}>{selectedIds.size} обрано</span>
          <button onClick={() => { setBulkModal("cost"); setBulkCostVal(""); }}
            style={{ padding: "6px 14px", borderRadius: 10, background: `${C.green}22`,
              border: `1px solid ${C.green}50`, color: C.green, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            Собівартість
          </button>
          <button onClick={() => setSelectedIds(new Set())}
            style={{ padding: "6px 14px", borderRadius: 10, background: "transparent",
              border: `1px solid ${C.border}`, color: C.text3, fontSize: 12, cursor: "pointer" }}>
            Зняти
          </button>
        </div>,
        document.body
      )}

      {/* Bulk cost modal */}
      {bulkModal === "cost" && createPortal(
        <div onClick={() => setBulkModal(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 10000,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ ...S.card, width: 340, padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Собівартість для {selectedIds.size} товарів</div>
            <div style={{ fontSize: 12, color: C.text3, marginBottom: 14 }}>
              Встановить однакову собівартість для всіх обраних товарів.
            </div>
            <input type="number" min="0" step="0.01" placeholder="Собівартість, ₴"
              value={bulkCostVal} onChange={e => setBulkCostVal(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submitBulkCost()}
              autoFocus
              style={{ ...S.input, width: "100%", marginBottom: 14, fontSize: 14 }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={submitBulkCost}
                disabled={!bulkCostVal || isNaN(parseFloat(bulkCostVal))}
                style={{ flex: 1, padding: "8px 0", borderRadius: 10,
                  background: (!bulkCostVal || isNaN(parseFloat(bulkCostVal))) ? C.bg1 : C.blue,
                  border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Застосувати
              </button>
              <button onClick={() => setBulkModal(null)}
                style={{ padding: "8px 14px", borderRadius: 10, background: C.bg1,
                  border: `1px solid ${C.border}`, color: C.text2, fontSize: 13, cursor: "pointer" }}>
                Скасувати
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
